import { useEffect, useState } from "react";
import AppLayout from "@/components/layout/AppLayout";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Plus, Trash2, Key, Shield, User as UserIcon, Lock, Unlock } from "lucide-react";
import { LoginEventsCard } from "@/components/LoginEventsCard";
import { formatDateTime } from "@/lib/constants";

export default function UsersPage() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [pwUser, setPwUser] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [form, setForm] = useState({ username: "", password: "", full_name: "", role: "data_entry" });
  const [eventsKey, setEventsKey] = useState(0);

  const load = () => api.get("/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);

  const unlock = async (u) => {
    try {
      await api.post(`/users/${u.id}/unlock`);
      toast.success(`${u.full_name} hesabının kilidi açıldı`);
      load();
      setEventsKey((k) => k + 1);
    } catch (e) { toast.error(e?.response?.data?.detail || "Kilit açılamadı"); }
  };

  const create = async () => {
    if (!form.username || !form.password || !form.full_name) return toast.error("Tüm alanlar zorunlu");
    try {
      await api.post("/users", form);
      toast.success("Kullanıcı eklendi");
      setOpen(false);
      setForm({ username: "", password: "", full_name: "", role: "data_entry" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Hata"); }
  };

  const remove = async (u) => {
    if (!window.confirm(`"${u.full_name}" silinsin mi?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Silindi");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Silinemedi"); }
  };

  const changePassword = async () => {
    if (!newPassword) return toast.error("Yeni şifre girin");
    try {
      await api.patch(`/users/${pwUser.id}/password`, { new_password: newPassword });
      toast.success("Şifre güncellendi");
      setPwOpen(false); setNewPassword("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Hata"); }
  };

  return (
    <AppLayout
      title="Kullanıcılar"
      subtitle="Sistem kullanıcıları ve yetki yönetimi"
      actions={
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-user-button" className="bg-emerald-700 hover:bg-emerald-800">
              <Plus className="h-4 w-4 mr-1.5" /> Yeni Kullanıcı
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="user-dialog">
            <DialogHeader><DialogTitle>Yeni Kullanıcı</DialogTitle></DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Ad Soyad *</Label>
                <Input value={form.full_name}
                       onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                       data-testid="user-fullname-input" />
              </div>
              <div>
                <Label>Kullanıcı Adı *</Label>
                <Input value={form.username}
                       onChange={(e) => setForm({ ...form, username: e.target.value })}
                       data-testid="user-username-input" />
              </div>
              <div>
                <Label>Şifre *</Label>
                <Input type="password" value={form.password}
                       onChange={(e) => setForm({ ...form, password: e.target.value })}
                       data-testid="user-password-input" />
              </div>
              <div>
                <Label>Rol</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Yönetici</SelectItem>
                    <SelectItem value="data_entry">Veri Giriş Personeli</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>İptal</Button>
              <Button onClick={create} className="bg-emerald-700 hover:bg-emerald-800"
                      data-testid="user-submit-button">Oluştur</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      }
    >
      <Card className="border border-slate-200 shadow-sm overflow-x-auto">
        <Table data-testid="users-table">
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead>Ad Soyad</TableHead>
              <TableHead>Kullanıcı Adı</TableHead>
              <TableHead>Rol</TableHead>
              <TableHead>Son Giriş</TableHead>
              <TableHead>Oluşturulma</TableHead>
              <TableHead className="w-32">İşlem</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody className="row-hover">
            {users.map((u) => (
              <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <div className="h-7 w-7 rounded-full bg-slate-100 flex items-center justify-center text-xs font-semibold text-slate-700">
                      {u.full_name?.[0]}
                    </div>
                    <span className="font-medium">{u.full_name}</span>
                    {u.id === me?.id && <Badge variant="secondary" className="text-[10px]">Siz</Badge>}
                  </div>
                </TableCell>
                <TableCell className="font-mono text-sm">{u.username}</TableCell>
                <TableCell>
                  {u.role === "admin" ? (
                    <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 hover:bg-emerald-100">
                      <Shield className="h-3 w-3 mr-1" /> Yönetici
                    </Badge>
                  ) : (
                    <Badge variant="outline"><UserIcon className="h-3 w-3 mr-1" /> Veri Giriş</Badge>
                  )}
                </TableCell>
                <TableCell className="text-xs whitespace-nowrap" data-testid={`user-last-login-${u.id}`}>
                  {u.last_login_at ? (
                    <span className="font-mono text-slate-600">{formatDateTime(u.last_login_at)}</span>
                  ) : <span className="text-slate-400">Henüz giriş yok</span>}
                  {u.locked_until && (
                    <div className="mt-1 flex w-fit items-center gap-1 px-2 py-0.5 rounded-full border bg-rose-50 text-rose-700 border-rose-200"
                         data-testid={`user-locked-${u.id}`}>
                      <Lock className="h-3 w-3" /> Kilitli · {formatDateTime(u.locked_until).slice(-8, -3)}'e kadar
                    </div>
                  )}
                  {!u.locked_until && u.failed > 0 && (
                    <div className="mt-1 text-amber-700" data-testid={`user-failed-${u.id}`}>{u.failed} hatalı deneme</div>
                  )}
                </TableCell>
                <TableCell className="text-xs text-slate-500 font-mono">
                  {u.created_at?.slice(0, 10).split("-").reverse().join(".")}
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    {(u.locked_until || u.failed > 0) && (
                      <Button variant="ghost" size="icon" onClick={() => unlock(u)} title="Kilidi aç"
                              data-testid={`unlock-user-${u.id}`}>
                        <Unlock className="h-4 w-4 text-blue-600" />
                      </Button>
                    )}
                    <Button variant="ghost" size="icon" onClick={() => { setPwUser(u); setPwOpen(true); }}
                            data-testid={`change-password-${u.id}`}>
                      <Key className="h-4 w-4" />
                    </Button>
                    {u.id !== me?.id && (
                      <Button variant="ghost" size="icon" onClick={() => remove(u)}
                              data-testid={`delete-user-${u.id}`}>
                        <Trash2 className="h-4 w-4 text-rose-600" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <LoginEventsCard refreshKey={eventsKey} />

      <Dialog open={pwOpen} onOpenChange={setPwOpen}>
        <DialogContent data-testid="change-password-dialog">
          <DialogHeader><DialogTitle>Şifre Değiştir — {pwUser?.full_name}</DialogTitle></DialogHeader>
          <div className="py-4">
            <Label>Yeni Şifre</Label>
            <Input type="password" value={newPassword}
                   onChange={(e) => setNewPassword(e.target.value)}
                   data-testid="new-password-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwOpen(false)}>İptal</Button>
            <Button onClick={changePassword} className="bg-emerald-700 hover:bg-emerald-800"
                    data-testid="change-password-submit">Güncelle</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
